import debounce from 'lodash.debounce'

/**
 * Collection accordion beside the masterplan.
 *
 * One row open at a time, because the drawing next to it can only show one
 * collection: opening a row closes the rest and cross-fades its plan in.
 *
 * Heights are measured into pixels the way the header menu's are — `height:
 * auto` can't be transitioned, and the page's other accordion already solves it
 * this way. Motion itself lives in css/base/plans.css, which also disables it
 * under `prefers-reduced-motion`; setting a height here stays instant either
 * way.
 */
export function initPlanLegend() {
    const root = document.querySelector('[data-villa-legend]')
    if (!root) return

    const rows = Array.from(root.querySelectorAll('[data-villa-toggle]')).map((toggle) => ({
        toggle,
        panel: document.getElementById(toggle.getAttribute('aria-controls')),
        plan: document.getElementById(toggle.dataset.planTarget),
    }))
    if (!rows.length || rows.some(({ panel, plan }) => !panel || !plan)) return

    const setRow = ({ toggle, panel, plan }, open) => {
        toggle.setAttribute('aria-expanded', String(open))
        panel.classList.toggle('is-open', open)
        // Measured on the content, not the panel: once the panel carries a pixel
        // height its own scrollHeight reports that height, not the content's.
        panel.style.height = open ? `${panel.firstElementChild.offsetHeight}px` : '0px'
        plan.setAttribute('aria-hidden', String(!open))
    }

    const openRow = (index) => rows.forEach((row, i) => setRow(row, i === index))

    // The markup ships one row open at `height: auto` so the page reads correctly
    // with no JS. Convert that to the pixel height everything animates against
    // before anything can move — from `auto` the first collapse wouldn't animate.
    const openIndex = rows.findIndex(({ toggle }) => toggle.getAttribute('aria-expanded') === 'true')
    openRow(openIndex)

    rows.forEach(({ toggle }, index) => {
        toggle.addEventListener('click', () => {
            // Pressing the open row again does nothing rather than collapsing it:
            // the masterplan beside these always shows one collection, so an
            // all-closed state would leave the drawing with nothing naming it.
            if (toggle.getAttribute('aria-expanded') === 'true') return
            openRow(index)
        })
    })

    // The open height is a fixed pixel value, so re-measure when the panel
    // reflows — a long collection name wraps at some widths and not others.
    window.addEventListener(
        'resize',
        debounce(() => {
            const open = rows.find(({ toggle }) => toggle.getAttribute('aria-expanded') === 'true')
            if (open) setRow(open, true)
        }, 100)
    )
}
